import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PlayCircle, ArrowLeft, Youtube } from 'lucide-react';

export const TUTORIAL_VIDEOS = [
  {
    id: 'KLBGpNvKVSc',
    title: 'Intro Movie',
    duration: '4:27',
    description: 'A quick tour of the app — the big picture of what you can do here and why it matters.',
    accent: 'bg-gradient-to-br from-[#2B6B7F] to-teal-500',
  },
  {
    id: 'HEA-CVoURs4',
    title: 'Discipleship Pathway',
    duration: '2:58',
    description: 'How to use the Pathway page to see where you are in your discipleship journey and what step comes next.',
    accent: 'bg-gradient-to-br from-teal-600 to-emerald-500',
  },
  {
    id: 'HuVmOQRcjhI',
    title: 'Vital Signs',
    duration: '1:40',
    description: 'Tracking your spiritual health with the Vital Signs — Pursue God, Engage Scripture, Love Others, Serve.',
    accent: 'bg-gradient-to-br from-amber-500 to-orange-500',
  },
  {
    id: 'iG4BqMldGbg',
    title: 'Sermon Notes',
    duration: '1:22',
    description: 'Where to find sermons and how to keep your own notes right inside the app.',
    accent: 'bg-gradient-to-br from-blue-600 to-indigo-500',
  },
  {
    id: 'Juosmo_R8oQ',
    title: 'Live Media',
    duration: '0:31',
    description: 'How to catch the live stream on Sunday and revisit past services.',
    accent: 'bg-gradient-to-br from-purple-600 to-fuchsia-500',
  },
  {
    id: '2oMR3vcn9dA',
    title: 'Resources',
    duration: '4:14',
    description: 'Everything under Resources — daily devotionals, prayer, Bible tools, Alpha, 7 Minute Seminary.',
    accent: 'bg-gradient-to-br from-teal-500 to-cyan-500',
  },
  {
    id: 'kO_o_36mG3g',
    title: 'For Parents',
    duration: '1:56',
    description: 'The Parents section — Life Maps, curriculum, phases, and how to disciple your kids at home.',
    accent: 'bg-gradient-to-br from-pink-500 to-rose-500',
  },
  {
    id: 'Q5iAgZeOywM',
    title: 'Prayer Wall',
    duration: '1:24',
    description: 'How to share prayer requests, pray for others, celebrate answered prayers, and use group prayers.',
    accent: 'bg-gradient-to-br from-fuchsia-500 to-purple-500',
  },
];

const AppTutorials = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to the specific tutorial when arriving with #videoId hash
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace('#', '');
    const tryScroll = (attempts = 0) => {
      const el = document.querySelector(`[data-testid="tutorial-card-${id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (attempts < 10) {
        setTimeout(() => tryScroll(attempts + 1), 100);
      }
    };
    setTimeout(() => tryScroll(), 200);
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      <Navigation />

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm text-slate-500 hover:text-[#2B6B7F] flex items-center gap-1 mb-2"
              data-testid="tutorials-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
            <h1 className="text-4xl font-heading font-bold text-[#2B6B7F] mb-1">
              App Tutorials
            </h1>
            <p className="text-slate-600 max-w-2xl">
              Short walk-throughs of every part of the app. Start with the Intro Movie or jump straight to
              whatever you want to learn about.
            </p>
          </div>
          <a
            href="https://www.youtube.com/playlist?list=PLB5hj-mfnnuY"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm self-start"
            data-testid="tutorials-playlist-link"
          >
            <Youtube className="w-4 h-4" />
            Full YouTube Playlist
          </a>
        </div>

        {/* Featured intro video */}
        <Card className="mb-8 border-2 border-[#2B6B7F]/20" data-testid="tutorial-intro-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#2B6B7F]">
              <PlayCircle className="w-6 h-6" />
              Start Here — Intro Movie
              <span className="ml-2 text-xs text-slate-400 font-normal">4:27</span>
            </CardTitle>
            <CardDescription>
              A quick tour of the app — the big picture of what you can do here and why it matters.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="aspect-video w-full bg-black rounded-lg overflow-hidden">
              <iframe
                src={`https://www.youtube.com/embed/${TUTORIAL_VIDEOS[0].id}`}
                title="Intro Movie"
                className="w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </CardContent>
        </Card>

        {/* Grid of remaining tutorials */}
        <h2 className="text-lg font-heading font-semibold text-slate-800 mb-4">
          More walk-throughs
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {TUTORIAL_VIDEOS.slice(1).map((v) => (
            <Card
              key={v.id}
              className="overflow-hidden hover:shadow-md transition-shadow"
              data-testid={`tutorial-card-${v.id}`}
            >
              <div className="aspect-video w-full bg-black">
                <iframe
                  src={`https://www.youtube.com/embed/${v.id}`}
                  title={v.title}
                  className="w-full h-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${v.accent}`}>
                    <PlayCircle className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-800 flex-1">{v.title}</h3>
                  <span className="text-xs text-slate-400">{v.duration}</span>
                </div>
                <p className="text-sm text-slate-600">{v.description}</p>
              </div>
            </Card>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-[#2B6B7F] hover:bg-[#245565]"
            data-testid="tutorials-done-btn"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AppTutorials;
